import { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AppManifestLauncher } from '@/lib/apps-mounts';

interface AppLauncherButtonProps {
  /** Manifest's launcher entry — drives label and icon. */
  launcher: AppManifestLauncher;
  /** App display name; used for the aria-label fallback. */
  appName: string;
  /** Called when the user clicks the button. The host should post
   *  `rumio:launcher:toggle` to the iframe — typically by calling the
   *  imperative handle on AppEmbed. */
  onToggle: () => void;
  /** Reflects the iframe's `rumio:launcher:state`. When true, the button
   *  shows a "close" affordance instead of the launcher icon, mirroring the
   *  drawer's open state. */
  open?: boolean;
}

/**
 * Floating launcher button rendered at host level (outside the iframe). The
 * iframe used to host this button itself, but `position: fixed` inside the
 * iframe pins to the iframe's body — and because Rumio auto-resizes iframes
 * to content height (rumio:height), `bottom: 6px` ended up far below the
 * visible viewport. Putting the button on the parent page fixes that.
 *
 * z-40 chosen to sit above page content but below the cart drawer
 * (z-50) and toast layer. Doesn't collide with admin Dippy (bottom-left).
 */
export function AppLauncherButton({ launcher, appName, onToggle, open }: AppLauncherButtonProps) {
  const label = launcher.label ?? appName;
  // We only ship the "sparkles" icon in v1.1 — manifest icon strings are
  // accepted but treated as a hint for future custom icons. Falling back
  // here keeps the surface stable as we add more.
  const Icon = open ? ChevronDown : Sparkles;
  return (
    <button
      type="button"
      aria-label={open ? `Close ${label}` : `Open ${label}`}
      aria-pressed={open ? 'true' : 'false'}
      data-testid="app-launcher-button"
      onClick={onToggle}
      className={cn(
        'fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full',
        'bg-primary text-primary-foreground shadow-lg transition-transform',
        open && 'scale-90',
      )}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}

/**
 * Convenience hook for hosts that want to track launcher open state without
 * managing it themselves. Pair with AppEmbed's `onLauncherStateChange`.
 */
export function useLauncherState(initialOpen = false) {
  const [open, setOpen] = useState(initialOpen);
  return { open, setOpen } as const;
}
